import { useEffect, useState, useMemo, useCallback } from 'react';
import { addDays, format, parseISO } from 'date-fns';
import { ApiError } from '@/lib/api';
import {
  createBooking,
  createRecurringSeries,
  fetchTutorSlots,
} from '@/lib/booking-api';
import { RecurrenceRuleSchema } from '@/lib/schemas/booking';
import type { PublicTutorCard } from '@/lib/schemas/tutor-profile';
import type { ChildResponse } from '@/lib/schemas/parent-profile';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { CalendarDays, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { createCalendarGrid } from '@/components/calendar/helpers';
import { Popover } from 'radix-ui';
import {
  slotsForLocalDate,
  formatSlotDuration,
  frequencyUsesDays,
  formatFrequencyAdverb,
  formatDayList,
  expandSeriesDates,
  formatDateReadable,
  getBookingSummaryText,
  DAY_KEYS,
  TutorSearchDropdown,
  RecurrenceSelector,
  SlotGrid,
} from './book-class-modal';
import type { BookableSlot, DayKey } from './book-class-modal';

export interface BookClassModalProps {
  open: boolean;
  onClose: () => void;
  accessToken: string;
  /** The parent's children to choose from. */
  childOptions: ChildResponse[];
  /** Preselects a tutor and hides the need to search (still removable). */
  initialTutor?: PublicTutorCard | null;
  /** Preselects a child by ID. */
  initialChildId?: string | null;
  /** Called after a successful booking, before the modal closes. */
  onBooked?: () => void | Promise<void>;
  /** Optional callback after a successful booking, passes details to show in success modal. */
  onSuccess?: (details: {
    summaryMessage: string;
    tutorName: string;
    childName: string;
  }) => void;
}

export function BookClassModal({
  open,
  onClose,
  accessToken,
  childOptions,
  initialTutor = null,
  initialChildId = null,
  onBooked,
  onSuccess,
}: BookClassModalProps) {
  const [bookFormData, setBookFormData] = useState({
    tutorId: '',
    childId: childOptions.length === 1 ? childOptions[0]?.id : '',
    date: '',
    slotStart: '', // ISO string of selected slot start
  });

  const [pickerYearAndMonth, setPickerYearAndMonth] = useState<[number, number]>(() => {
    const now = new Date();
    return [now.getFullYear(), now.getMonth() + 1];
  });
  const [showDatePicker, setShowDatePicker] = useState(false);

  const [pickerYear, pickerMonth] = pickerYearAndMonth;
  const calendarGrid = useMemo(() => createCalendarGrid(pickerYear, pickerMonth), [pickerYear, pickerMonth]);

  const handlePickerPrevMonth = () => {
    setPickerYearAndMonth(([y, m]) => {
      const nextM = m - 1;
      return nextM === 0 ? [y - 1, 12] : [y, nextM];
    });
  };

  const handlePickerNextMonth = () => {
    setPickerYearAndMonth(([y, m]) => {
      const nextM = m + 1;
      return nextM === 13 ? [y + 1, 1] : [y, nextM];
    });
  };

  const pickerMonthName = format(new Date(pickerYear, pickerMonth - 1, 1), 'MMMM yyyy');
  const [isSeriesMode, setIsSeriesMode] = useState(false);
  const [seriesFormData, setSeriesFormData] = useState({
    repeatPattern: 'weekly',
    numberOfClasses: '4',
  });
  const [seriesDays, setSeriesDays] = useState<DayKey[]>([]);

  const [formError, setFormError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [selectedTutorData, setSelectedTutorData] = useState<PublicTutorCard | null>(null);
  const [availableSlots, setAvailableSlots] = useState<BookableSlot[]>([]);
  const [isLoadingSlots, setIsLoadingSlots] = useState(false);

  const resetForm = () => {
    setBookFormData({
      tutorId: '',
      childId: childOptions.length === 1 ? childOptions[0]?.id : '',
      date: '',
      slotStart: '',
    });
    setIsSeriesMode(false);
    setSeriesFormData({ repeatPattern: 'weekly', numberOfClasses: '4' });
    setSeriesDays([]);
    setSelectedTutorData(null);
    setFormError(null);
    setSuccessMessage(null);
    const now = new Date();
    setPickerYearAndMonth([now.getFullYear(), now.getMonth() + 1]);
    setShowDatePicker(false);
  };

  // Apply the preselected tutor each time the modal opens fresh.
  useEffect(() => {
    if (!open) return;
    if (initialTutor) {
      setSelectedTutorData(initialTutor);
      setBookFormData((f) => ({ ...f, tutorId: initialTutor.id }));
    }
  }, [open, initialTutor]);

  // Preselect child by initialChildId or auto-select if single child option
  useEffect(() => {
    if (!open) return;
    if (initialChildId && childOptions.some((c) => c.id === initialChildId)) {
      setBookFormData((f) => ({ ...f, childId: initialChildId }));
    } else if (childOptions.length === 1 && childOptions[0]?.id) {
      setBookFormData((f) => ({ ...f, childId: childOptions[0]?.id }));
    }
  }, [open, initialChildId, childOptions]);

  const handleClose = () => {
    resetForm();
    onClose();
  };

  // Fetch available slots from API
  useEffect(() => {
    const tutorId = selectedTutorData?.id;
    if (!open || !tutorId || !bookFormData.date || !accessToken) {
      setAvailableSlots([]);
      return;
    }

    let cancelled = false;
    setIsLoadingSlots(true);

    const base = parseISO(bookFormData.date);
    const from = addDays(base, -1).toISOString();
    const to = addDays(base, 2).toISOString();

    (async () => {
      try {
        const slots = await fetchTutorSlots(accessToken, tutorId, from, to);
        if (!cancelled) {
          setAvailableSlots(slotsForLocalDate(slots, bookFormData.date));
        }
      } catch {
        if (!cancelled) {
          setAvailableSlots([]);
          setFormError("Couldn't load this tutor's availability. Please try again.");
        }
      } finally {
        if (!cancelled) {
          setIsLoadingSlots(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, selectedTutorData?.id, bookFormData.date, accessToken]);

  const selectedSlot = availableSlots.find(
    (s) => s.startUtc.toISOString() === bookFormData.slotStart,
  );

  // Default the day picker to the first class's weekday once a slot is chosen.
  useEffect(() => {
    if (isSeriesMode && selectedSlot && seriesDays.length === 0) {
      setSeriesDays([DAY_KEYS[selectedSlot.startUtc.getUTCDay()] as DayKey]);
    }
  }, [isSeriesMode, bookFormData.slotStart, selectedSlot, seriesDays.length]);

  const seriesNeedsDays = isSeriesMode && frequencyUsesDays(seriesFormData.repeatPattern);
  const seriesCount = parseInt(seriesFormData.numberOfClasses, 10) || 0;
  const seriesDates =
    isSeriesMode && selectedSlot
      ? expandSeriesDates(
          selectedSlot.startUtc,
          seriesFormData.repeatPattern,
          seriesNeedsDays ? seriesDays : [],
          seriesCount,
        )
      : [];

  const selectedChild = childOptions.find((c) => c.id === bookFormData.childId);

  const canShowSummary = Boolean(
    bookFormData.tutorId && bookFormData.childId && bookFormData.date && selectedSlot,
  );

  const classTitle = selectedTutorData
    ? `${selectedTutorData.subjects[0] || 'Class'} with ${selectedTutorData.displayName}`
    : '';

  const handleSelectTutor = useCallback((tutor: PublicTutorCard | null) => {
    setSelectedTutorData(tutor);
    setBookFormData((f) => ({
      ...f,
      tutorId: tutor ? tutor.id : '',
      slotStart: '',
    }));
    setFormError(null);
  }, []);

  const handleSelectSlotStart = useCallback((slotStart: string) => {
    setBookFormData((f) => ({ ...f, slotStart }));
    setFormError(null);
  }, []);

  const handleChangeRepeatPattern = useCallback((val: string) => {
    setSeriesFormData((f) => ({ ...f, repeatPattern: val }));
  }, []);

  const handleChangeNumberOfClasses = useCallback((val: string) => {
    setSeriesFormData((f) => ({ ...f, numberOfClasses: val }));
  }, []);

  const handleSubmitBooking = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!bookFormData.tutorId) {
      setFormError('Please select a tutor.');
      return;
    }
    if (!bookFormData.childId) {
      setFormError('Please select a child.');
      return;
    }
    if (!bookFormData.date) {
      setFormError('Please select a date.');
      return;
    }
    if (!selectedSlot) {
      setFormError('Please pick one of the available time slots.');
      return;
    }
    if (seriesNeedsDays && seriesDays.length === 0) {
      setFormError('Please select at least one day of the week for the series.');
      return;
    }
    if (!accessToken) {
      setFormError('Your session has expired. Please log in again.');
      return;
    }

    setFormError(null);
    setIsSubmitting(true);

    try {
      const parentTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const slotStartUtc = selectedSlot.startUtc.toISOString();
      const slotEndUtc = selectedSlot.endUtc.toISOString();
      const subject = selectedTutorData?.subjects[0];

      const common = {
        tutorId: bookFormData.tutorId,
        childId: bookFormData.childId,
        slotStartUtc,
        slotEndUtc,
        type: 'paid' as const,
        tutorTimeZone: selectedTutorData?.homeTz ?? parentTimeZone,
        parentTimeZone,
        ...(classTitle ? { classTitle } : {}),
        ...(subject ? { subject } : {}),
      };

      if (isSeriesMode) {
        const frequency = RecurrenceRuleSchema.shape.frequency.parse(
          seriesFormData.repeatPattern,
        );
        await createRecurringSeries(accessToken, {
          ...common,
          recurrenceRule: {
            frequency,
            count: parseInt(seriesFormData.numberOfClasses, 10),
            seriesIndex: 0,
            ...(seriesNeedsDays && seriesDays.length > 0 ? { days: seriesDays } : {}),
          },
        });
      } else {
        await createBooking(accessToken, common);
      }

      const summaryText = getBookingSummaryText({
        isSeriesMode,
        seriesDates,
        selectedSlot,
        repeatPattern: seriesFormData.repeatPattern,
        seriesNeedsDays,
        seriesDays,
        date: bookFormData.date,
        verb: 'Booked',
      });

      await onBooked?.();

      if (onSuccess) {
        onSuccess({
          summaryMessage: summaryText,
          tutorName: selectedTutorData?.displayName ?? 'Tutor',
          childName: selectedChild?.name ?? 'Child',
        });
        handleClose();
      } else {
        setSuccessMessage(
          isSeriesMode
            ? `Series of ${seriesFormData.numberOfClasses} classes booked successfully!`
            : 'Booking created successfully!',
        );
        setTimeout(() => {
          handleClose();
        }, 1500);
      }
    } catch (err: unknown) {
      setFormError(
        err instanceof ApiError
          ? err.message
          : 'Failed to create booking. Please try again.',
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} title="Book a Class" maxWidth="max-w-2xl">
      {successMessage && (
        <div className="mb-3 rounded-lg border border-green-200 bg-green-50 p-3 text-sm text-green-700 dark:border-green-900/50 dark:bg-green-950/30 dark:text-green-400 flex items-center gap-2 animate-fade-in">
          <span>✓</span>
          <span>{successMessage}</span>
        </div>
      )}
      <form className="flex flex-col gap-6" onSubmit={handleSubmitBooking}>
        {/* Step 1 & 2: Child and Tutor Selection */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 items-start animate-fade-in">
          {/* Select Child */}
          <div>
            <Label htmlFor="child-select" className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider block mb-1">
              1. Select Child
            </Label>
            {selectedChild ? (
              <div className="mt-1 flex items-center gap-2 h-9">
                <div className="inline-flex items-center gap-2 rounded-full bg-blue-50 h-9 pl-0 pr-3 dark:bg-blue-950/50 overflow-hidden border border-blue-100 dark:border-blue-800/50">
                  <div className="flex h-9 w-9 items-center justify-center rounded-full bg-blue-200 text-sm font-semibold text-blue-700 dark:bg-blue-900/80 dark:text-blue-200 shrink-0">
                    {selectedChild.name.charAt(0).toUpperCase()}
                  </div>
                  <span className="text-sm font-medium text-blue-900 dark:text-blue-100">
                    {selectedChild.name}
                  </span>
                  {childOptions.length > 1 && (
                    <button
                      type="button"
                      onClick={() => {
                        setBookFormData((f) => ({ ...f, childId: '' }));
                        setFormError(null);
                      }}
                      className="ml-1 rounded-full p-0.5 text-blue-600 hover:bg-blue-100 dark:text-blue-400 dark:hover:bg-blue-900/40 cursor-pointer"
                      aria-label="Remove child"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <div className="relative mt-1">
                <select
                  id="child-select"
                  value={bookFormData.childId}
                  onChange={(e) => {
                    setBookFormData((f) => ({ ...f, childId: e.target.value }));
                    setFormError(null);
                  }}
                  className="h-9 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-brand-600 focus:ring-offset-2 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100 dark:focus:ring-brand-600 dark:focus:ring-offset-slate-800"
                >
                  <option value="">Select a child</option>
                  {childOptions.map((child) => (
                    <option key={child.id} value={child.id}>
                      {child.name} ({child.age} yrs, {child.grade})
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {/* Select Tutor */}
          <div>
            <Label htmlFor="tutor-search" className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider block mb-1">
              2. Select Tutor
            </Label>
            <TutorSearchDropdown
              accessToken={accessToken}
              selectedTutor={selectedTutorData}
              onSelectTutor={handleSelectTutor}
              open={open}
            />
          </div>
        </div>

        {/* Recurrence Toggle */}
        <div className="flex items-center justify-between gap-4 rounded-xl border border-slate-200 bg-slate-50/70 px-4 py-3 dark:border-slate-700 dark:bg-slate-800/40">
          <div>
            <p id="series-toggle-label" className="text-sm font-medium text-slate-900 dark:text-slate-100">
              Book a series of classes
            </p>
            <p className="text-xs text-slate-505 dark:text-slate-400">
              Repeat this class automatically on a schedule
            </p>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={isSeriesMode}
            aria-labelledby="series-toggle-label"
            onClick={() => setIsSeriesMode((v) => !v)}
            className={cn(
              'relative h-6 w-11 shrink-0 rounded-full transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-slate-800',
              isSeriesMode ? 'bg-blue-600' : 'bg-slate-300 dark:bg-slate-600',
            )}
          >
            <span
              className={cn(
                'absolute left-0.5 top-0.5 h-5 w-5 rounded-full bg-white shadow-sm transition-transform duration-200',
                isSeriesMode && 'translate-x-5',
              )}
            />
          </button>
        </div>

        {/* Step 3: Date & Recurrence configs */}
        <div className="rounded-xl border border-slate-200 p-4 dark:border-slate-700 bg-white dark:bg-slate-800/10">
          <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-3 block">
            3. Date & Schedule Settings
          </p>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3">
            {/* Date Picker */}
            <div>
              <Label htmlFor="date-trigger" className="text-slate-655 dark:text-slate-400 text-xs">
                {isSeriesMode ? 'Start Date' : 'Date'}
              </Label>
              <div className="mt-1">
                <Popover.Root open={showDatePicker} onOpenChange={setShowDatePicker}>
                  <Popover.Trigger asChild>
                    <button
                      id="date-trigger"
                      type="button"
                      className={cn(
                        "flex h-9 w-full items-center justify-between rounded-md border border-slate-305 bg-white px-3 py-1.5 text-sm text-slate-900 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100 dark:focus:ring-blue-500",
                        !bookFormData.date && "text-slate-400 dark:text-slate-505"
                      )}
                    >
                      <span>
                        {bookFormData.date
                          ? format(parseISO(bookFormData.date), 'PPP')
                          : 'Select a date'}
                      </span>
                      <CalendarDays className="h-4 w-4 text-slate-400 dark:text-slate-505" />
                    </button>
                  </Popover.Trigger>

                  <Popover.Portal>
                    <Popover.Content
                      side="bottom"
                      align="start"
                      sideOffset={4}
                      className="z-50 rounded-lg border border-slate-200 bg-white p-3 shadow-lg dark:border-slate-700 dark:bg-slate-800 w-72 animate-in fade-in-0 zoom-in-95 duration-200"
                    >
                      <div className="flex flex-col gap-2">
                        <div className="flex items-center justify-between px-1">
                          <span className="text-sm font-semibold text-slate-800 dark:text-slate-200">
                            {pickerMonthName}
                          </span>
                          <div className="flex gap-1">
                            <button
                              type="button"
                              onClick={handlePickerPrevMonth}
                              className="rounded p-1 text-slate-550 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-700"
                              aria-label="Previous month"
                            >
                              <ChevronLeft size={16} />
                            </button>
                            <button
                              type="button"
                              onClick={handlePickerNextMonth}
                              className="rounded p-1 text-slate-550 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-700"
                              aria-label="Next month"
                            >
                              <ChevronRight size={16} />
                            </button>
                          </div>
                        </div>

                        <div className="grid grid-cols-7 text-center text-[10px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-505">
                          {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((d) => (
                            <div key={d} className="py-1">{d}</div>
                          ))}
                        </div>

                        <div className="grid grid-cols-7 gap-1">
                          {calendarGrid.map((day) => {
                            const todayStr = format(new Date(), 'yyyy-MM-dd');
                            const isPast = day.dateString < todayStr;
                            const isSelected = bookFormData.date === day.dateString;
                            const isCurrentMonth = day.isCurrentMonth;

                            return (
                              <button
                                key={day.dateString}
                                type="button"
                                disabled={isPast}
                                onClick={() => {
                                  setBookFormData((f) => ({ ...f, date: day.dateString, slotStart: '' }));
                                  setFormError(null);
                                  setShowDatePicker(false);
                                }}
                                className={cn(
                                  "flex h-8 w-8 items-center justify-center rounded-full text-xs font-medium transition-all focus:outline-none",
                                  isPast && "cursor-not-allowed text-slate-300 dark:text-slate-600 opacity-40",
                                  !isPast && !isSelected && isCurrentMonth && "text-slate-700 hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-700",
                                  !isPast && !isSelected && !isCurrentMonth && "text-slate-400 hover:bg-slate-100 dark:text-slate-505 dark:hover:bg-slate-700",
                                  isSelected && "bg-blue-600 text-white shadow-sm hover:bg-blue-700 dark:bg-blue-505 dark:hover:bg-blue-600"
                                )}
                              >
                                {day.dayOfMonth}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    </Popover.Content>
                  </Popover.Portal>
                </Popover.Root>
              </div>
            </div>

            {/* Recurrence config */}
            {isSeriesMode && (
              <RecurrenceSelector
                repeatPattern={seriesFormData.repeatPattern}
                onChangeRepeatPattern={handleChangeRepeatPattern}
                numberOfClasses={seriesFormData.numberOfClasses}
                onChangeNumberOfClasses={handleChangeNumberOfClasses}
                seriesDays={seriesDays}
                onChangeSeriesDays={setSeriesDays}
              />
            )}
          </div>
        </div>

        {/* Step 4: Available Slots */}
        <SlotGrid
          selectedTutorName={selectedTutorData?.displayName}
          selectedDate={bookFormData.date}
          isLoadingSlots={isLoadingSlots}
          availableSlots={availableSlots}
          selectedSlotStart={bookFormData.slotStart}
          onSelectSlotStart={handleSelectSlotStart}
        />

        {/* Form Error message panel */}
        {formError && (
          <div role="alert" className="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-3 dark:border-red-900/50 dark:bg-red-950/30 animate-fade-in">
            <span aria-hidden="true" className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-red-100 text-xs font-bold text-red-650 dark:bg-red-900/50 dark:text-red-400">
              !
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-red-800 dark:text-red-300">
                Couldn't book this class
              </p>
              <p className="mt-0.5 text-sm text-red-750 dark:text-red-400">{formError}</p>
            </div>
            <button
              type="button"
              onClick={() => setFormError(null)}
              aria-label="Dismiss error"
              className="flex-shrink-0 rounded p-0.5 text-red-405 hover:bg-red-100 hover:text-red-600 dark:hover:bg-red-909/40 cursor-pointer"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        )}

        {/* Booking Summary */}
        {canShowSummary && selectedSlot && (
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-800/80 animate-fade-in">
            <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
              <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                {classTitle}
              </p>
              <p className="text-xs font-medium text-slate-505 dark:text-slate-400">
                For {selectedChild?.name}
              </p>
            </div>

            {isSeriesMode && seriesDates.length > 0 ? (
              seriesDates.length === 1 ? (
                <p className="mt-2 border-t border-blue-200 pt-2 text-sm leading-relaxed text-slate-705 dark:border-blue-900/50 dark:text-slate-300">
                  Booking{' '}
                  <span className="font-semibold text-slate-900 dark:text-slate-100">
                    1 class
                  </span>{' '}
                  for{' '}
                  <span className="font-semibold text-slate-900 dark:text-slate-100">
                    {format(seriesDates[0]!, 'd MMMM yyyy')}
                  </span>
                  , {format(selectedSlot.startUtc, 'h:mm a')} –{' '}
                  {format(selectedSlot.endUtc, 'h:mm a')} ({formatSlotDuration(selectedSlot)}).
                </p>
              ) : (
                <p className="mt-2 border-t border-blue-205 pt-2 text-sm leading-relaxed text-slate-705 dark:border-blue-900/50 dark:text-slate-300">
                  Booking{' '}
                  <span className="font-semibold text-slate-900 dark:text-slate-100">
                    {seriesDates.length} classes
                  </span>{' '}
                  {formatFrequencyAdverb(seriesFormData.repeatPattern)}
                  {seriesNeedsDays && seriesDays.length > 0 && (
                    <> every <span className="font-semibold text-slate-900 dark:text-slate-100">{formatDayList(seriesDays)}</span></>
                  )}
                  , from{' '}
                  <span className="font-semibold text-slate-900 dark:text-slate-100">
                    {format(seriesDates[0]!, 'd MMMM yyyy')}
                  </span>{' '}
                  to{' '}
                  <span className="font-semibold text-slate-900 dark:text-slate-100">
                    {format(seriesDates[seriesDates.length - 1]!, 'd MMMM yyyy')}
                  </span>
                  , {format(selectedSlot.startUtc, 'h:mm a')} –{' '}
                  {format(selectedSlot.endUtc, 'h:mm a')} ({formatSlotDuration(selectedSlot)} each).
                </p>
              )
            ) : (
              <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 border-t border-slate-200 pt-2 text-sm dark:border-slate-700 sm:grid-cols-3">
                <div>
                  <p className="text-xs text-slate-505 dark:text-slate-400">Date</p>
                  <p className="font-medium text-slate-900 dark:text-slate-100">
                    {formatDateReadable(bookFormData.date)}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-slate-505 dark:text-slate-400">Time</p>
                  <p className="font-medium text-slate-900 dark:text-slate-100">
                    {format(selectedSlot.startUtc, 'h:mm a')} –{' '}
                    {format(selectedSlot.endUtc, 'h:mm a')}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-slate-550 dark:text-slate-400">Duration</p>
                  <p className="font-medium text-blue-600 dark:text-blue-400">
                    {formatSlotDuration(selectedSlot)}
                  </p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Actions panel */}
        <div className="flex justify-end gap-3 border-t border-slate-200 pt-4 dark:border-slate-700">
          <Button type="button" variant="outline" onClick={handleClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button type="submit" disabled={isSubmitting || !canShowSummary}>
            {isSubmitting ? 'Booking...' : 'Confirm Booking'}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
